import { NgService } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { IRootScope } from "@models";

export interface IDragAndDrop {
    onDragEnter?: () => void;
    onDragLeave?: () => void;
    onDropFiles?: (files: File[]) => void;
}

@NgService({
    token: Token.DragAndDropService,
    dependencies: [
        Token.$RootScope
    ]
})
export class DragAndDropService {

    private subscribers: IDragAndDrop[] = [];
    private globalDragCounter = 0;
    private listenersInitialized = false;

    constructor(
        private $rootScope: IRootScope,
    ) { }

    private isDragging = false;

    public setDragging(value: boolean) {
        this.isDragging = value;
        this.broadcastDragging();
    }

    public isDraggingActive(): boolean {
        return this.isDragging;
    }

    private broadcastDragging() {
        this.$rootScope.$broadcast('globalDragStateChanged', this.isDragging);
    }


    public subscribe(callbacks: IDragAndDrop): () => void {
        this.subscribers.push(callbacks);
        this.ensureListeners();

        // return unsubscribe function
        return () => {
            this.subscribers = this.subscribers.filter(s => s !== callbacks);
        };
    }

    private ensureListeners() {
        if (this.listenersInitialized) return;
        this.listenersInitialized = true;

        window.addEventListener("dragenter", this.onDragEnter);
        window.addEventListener("dragover", this.onDragOver);
        window.addEventListener("dragleave", this.onDragLeave);
        window.addEventListener("drop", this.onDrop);
    }

    private onDragEnter = (e: DragEvent) => {
        if (!e.dataTransfer?.types?.includes("Files")) return;

        this.globalDragCounter++;

        this.subscribers.forEach(s => s.onDragEnter?.());
        this.safeApply();
    };

    private onDragOver = (e: DragEvent) => {
        e.preventDefault();
        if (!this.isDragging) this.setDragging(true);
    };

    private onDragLeave = (e: DragEvent) => {
        this.globalDragCounter--;

        if (this.globalDragCounter <= 0) {
            this.globalDragCounter = 0;
            this.setDragging(false);
            this.subscribers.forEach(s => s.onDragLeave?.());
            this.safeApply();
        }
    };

    private onDrop = (e: DragEvent) => {
        e.preventDefault();

        this.globalDragCounter = 0;
        this.setDragging(false);

        this.subscribers.forEach(s => s.onDragLeave?.());
        this.safeApply();

        if (e.dataTransfer?.files?.length) {
            const files = Array.from(e.dataTransfer.files);
            this.subscribers.forEach(s => s.onDropFiles?.(files));
        }
    };

    private safeApply() {
        const phase = this.$rootScope.$root.$$phase;
        if (phase !== '$apply' && phase !== '$digest') {
            this.$rootScope.$applyAsync();
        }
    }
}
